/**
 * Sprint 34 (GT-COMMS-DOMAINS-FOUNDATION-01) — admin-side mirror of
 * the tenant-slug helpers in
 * `services/api/src/modules/tenancy/slug.ts`.
 *
 * Mirrors the validation rules + reserved set EXACTLY so the future
 * Tenant Edit drawer can validate slugs against the same contract
 * the API will accept once the `tenants.slug` column lands. Source
 * guard tests pin both files in lockstep.
 *
 * No network call. Pure helper. Used today by the Tenant 360°
 * "Domain readiness" card to build the future-URL preview.
 */

import { RESERVED_TENANT_SLUGS } from "@genius-tracker/tenancy";

/**
 * Re-exported from `@genius-tracker/tenancy` — the single canonical list,
 * shared with `services/api`. This module used to carry its own copy; the two
 * agreed only because someone kept remembering to edit both.
 */
export { RESERVED_TENANT_SLUGS };

export type TenantSlugValidationReason =
  | "empty"
  | "too_short"
  | "too_long"
  | "invalid_chars"
  | "leading_digit"
  | "leading_or_trailing_hyphen"
  | "consecutive_hyphens"
  | "reserved";

export interface TenantSlugValidation {
  readonly cleaned: string;
  readonly reason: TenantSlugValidationReason | null;
}

export const SLUG_MIN_LENGTH = 2;
export const SLUG_MAX_LENGTH = 62;

export function validateTenantSlug(candidate: string | null | undefined): TenantSlugValidation {
  if (candidate === null || candidate === undefined) {
    return { cleaned: "", reason: "empty" };
  }
  const cleaned = candidate.trim().toLowerCase();
  if (cleaned.length === 0) return { cleaned, reason: "empty" };
  if (cleaned.length < SLUG_MIN_LENGTH) return { cleaned, reason: "too_short" };
  if (cleaned.length > SLUG_MAX_LENGTH) return { cleaned, reason: "too_long" };
  if (!/^[a-z0-9-]+$/.test(cleaned)) return { cleaned, reason: "invalid_chars" };
  if (/^[0-9]/.test(cleaned)) return { cleaned, reason: "leading_digit" };
  if (cleaned.startsWith("-") || cleaned.endsWith("-")) {
    return { cleaned, reason: "leading_or_trailing_hyphen" };
  }
  if (cleaned.includes("--")) return { cleaned, reason: "consecutive_hyphens" };
  if (RESERVED_TENANT_SLUGS.has(cleaned)) return { cleaned, reason: "reserved" };
  return { cleaned, reason: null };
}

export function buildTenantUrl(slug: string, baseDomain: string): string | null {
  const validation = validateTenantSlug(slug);
  if (validation.reason !== null) return null;
  if (!baseDomain || baseDomain.includes("/")) return null;
  return `https://${validation.cleaned}.${baseDomain.trim().toLowerCase()}`;
}

/**
 * Default base domain for the future tenant URL pattern. Lives in
 * code so the readiness card and tests can render the same target
 * without touching env or DNS.
 */
export const DEFAULT_TENANT_BASE_DOMAIN = "genius-tracker.com";

/**
 * Sprint 37 — produce a slug-safe candidate from a free-form
 * display name. Mirrors the SQL backfill in migration 0040 and the
 * `slugifyTenantDisplayName` helper in
 * `services/api/src/modules/tenancy/slug.ts`. Pure function — used
 * by the Tenant Edit drawer to suggest a sensible default slug when
 * the operator has not set one yet.
 */
export function slugifyTenantDisplayName(displayName: string | null | undefined): string {
  let base = (displayName ?? "").toLowerCase();
  base = base.replace(/[^a-z0-9-]+/g, "-");
  base = base.replace(/-+/g, "-");
  base = base.replace(/^-+|-+$/g, "");

  if (base === "") base = "tenant";

  if (/^[0-9]/.test(base)) base = `t-${base}`;

  if (base.length > SLUG_MAX_LENGTH) {
    base = base.slice(0, SLUG_MAX_LENGTH);
    base = base.replace(/-+$/g, "");
  }

  if (RESERVED_TENANT_SLUGS.has(base)) base = `${base}-co`;

  if (base.length < SLUG_MIN_LENGTH) base = `${base}co`;

  return base;
}
